import { Alert, Button, Drawer, InputNumber, message, Popconfirm, Spin, Typography } from 'antd';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useState } from 'react';

import type { ClubPeriod, MemberRow } from './types';
import { formatMNT, formatPeriod, queryErrorMessage } from './types';

const { Text } = Typography;

interface AheadLine extends ClubPeriod {
  full_amount: number;
  discount_amount: number;
  amount: number;
  paid: boolean;
}

interface AheadInfo {
  // How many Club months in the schedule are still to bill.
  available: number;
  lines: AheadLine[];
  total_amount: number;
  card_balance: number;
}

// Pay ahead (CONTEXT.md): bills a member's next Club months now and pays what
// the card balance covers; the rest stays an Outstanding payment. Any number
// of times — an admin may add a month a parent didn't.
export default function PayAheadDrawer({
  member,
  onClose,
  onDone,
}: {
  member: MemberRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { runQuery } = useScreen();
  const [preview, setPreview] = useState<AheadInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    runQuery<AheadInfo>('club_pay_ahead', { clubStudentId: member.id, preview: true })
      .then(([row]) => setPreview(row ?? null))
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [member.id, runQuery]);

  const chosen = preview?.lines.slice(0, months) ?? [];
  const total = chosen.reduce((sum, l) => sum + l.amount, 0);
  const short = preview != null && preview.card_balance < total;

  const submit = () => {
    setSubmitting(true);
    setError(null);
    runQuery<AheadInfo>('club_pay_ahead', { clubStudentId: member.id, months })
      .then(([row]) => {
        const unpaid = row?.lines.filter((l) => !l.paid).length ?? 0;
        if (unpaid)
          message.warning(`${months} сар нэхэмжлэгдлээ, ${unpaid} нь төлөгдөөгүй үлдлээ.`);
        else message.success(`${months} сарын төлбөр төлөгдлөө.`);
        onDone();
      })
      .catch((e) => setError(queryErrorMessage(e)))
      .finally(() => setSubmitting(false));
  };

  return (
    <Drawer title="Урьдчилж төлөх" open onClose={onClose} width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Сурагч
          </Text>
          <div style={{ fontWeight: 600 }}>{member.student_name}</div>
        </div>

        {loading && <Spin />}

        {!loading && preview && preview.available === 0 && (
          <Alert type="info" showIcon message="Хуваарьт урьдчилж төлөх сар алга." />
        )}

        {!loading && preview && preview.available > 0 && (
          <>
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
                Хэдэн сар (хуваарьт {preview.available} сар байна)
              </Text>
              <InputNumber
                min={1}
                max={preview.available}
                value={months}
                onChange={(v) => setMonths(v ?? 1)}
              />
            </div>
            <div
              style={{
                borderRadius: 8,
                padding: 12,
                background: short ? '#fff7e6' : '#f6ffed',
                border: `1px solid ${short ? '#ffd591' : '#b7eb8f'}`,
              }}
            >
              {chosen.map((l) => (
                <div
                  key={String(l.period_start)}
                  style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}
                >
                  <span>{formatPeriod(l)}</span>
                  <span>{formatMNT(l.amount)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span>Нийт</span>
                <strong>{formatMNT(total)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span>Картын үлдэгдэл</span>
                <strong>{formatMNT(preview.card_balance)}</strong>
              </div>
              {short && (
                <p style={{ margin: '8px 0 0', color: '#d46b08', fontSize: 12 }}>
                  Үлдэгдэл хүрэлцэхгүй — хүрэлцэхгүй сарууд төлөгдөөгүй үлдэж, цэнэглэхэд автоматаар
                  төлөгдөнө.
                </p>
              )}
            </div>
          </>
        )}

        {error && <Alert type="error" showIcon message={error} />}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button onClick={onClose} disabled={submitting}>
            Болих
          </Button>
          <Popconfirm
            title="Урьдчилж нэхэмжлэх үү?"
            description={`${months} сарын ${formatMNT(total)} нэхэмжилж, үлдэгдлээс төлнө.`}
            okText="Нэхэмжлэх"
            cancelText="Болих"
            onConfirm={submit}
            disabled={!preview?.available}
          >
            <Button
              type="primary"
              loading={submitting}
              disabled={loading || !preview?.available || submitting}
            >
              Урьдчилж төлөх
            </Button>
          </Popconfirm>
        </div>
      </div>
    </Drawer>
  );
}
